import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import moment from 'moment'
import ClipLoader from 'react-spinners/ClipLoader'
import { toast } from 'react-toastify'
import { UserMinus, UserPlus, Trash2 } from 'react-feather'
import { Icon as IconifyIcon } from '@iconify/react'
import PrivateLayout from '../components/PrivateLayout'
import Footer from '../components/Footer'
import uploadIcon from '../assets/scss/img/icons/cloud-upload.svg'
import OrganizationSummary from '../components/OrganizationSummary'
import { useAddWorkMutation, useSearchWorkMutation, useUploadWorkAttachmentMutation } from '../store/api'
import {
  AuthorCreationData,
  AuthorData,
  FilterOption,
  SearchType,
  WorkCreationRequest,
  WorkData,
  WorkSearchRequest,
} from '../types'
import { convertWorkType } from '../utils'

const maxFileSize = 1024 * 1024 * 50

const languageOptions: FilterOption[] = [
  {
    label: 'Select language',
    value: '',
  },
  {
    label: 'English',
    value: 'en',
  },
  {
    label: 'French',
    value: 'fr',
  },
  {
    label: 'Japanese',
    value: 'ja',
  },
]

const roleOptions: FilterOption[] = [
  {
    label: 'Select role',
    value: '',
  },
  {
    label: 'Primary',
    value: 'primary',
  },
  {
    label: 'Additional',
    value: 'additional',
  },
]

const workTypeOptions: FilterOption[] = [
  {
    label: 'Select Work Product Type',
    value: '',
  },
  {
    label: 'Journal Article',
    value: 'journal-article',
  },
  {
    label: 'Book Chapter',
    value: 'book-chapter',
  },
  {
    label: 'Book',
    value: 'book',
  },
  {
    label: 'Preprint',
    value: 'preprint',
  },
  {
    label: 'Dataset',
    value: 'dataset',
  },
  {
    label: 'Protocol',
    value: 'protocol',
  },
  {
    label: 'Presentation',
    value: 'presentation',
  },
  {
    label: 'Report',
    value: 'report',
  },
  {
    label: 'Software code',
    value: 'software-code',
  },
  {
    label: 'Audio',
    value: 'audio',
  },
  {
    label: 'Video',
    value: 'video',
  },
  {
    label: 'Website',
    value: 'website',
  },
  {
    label: 'Other',
    value: 'other',
  },
]

const defaultAuthorData: AuthorCreationData = {
  firstName: '',
  lastName: '',
  orcId: '',
  role: '',
}

const AddWork = (): JSX.Element => {
  const navigate = useNavigate()
  const { grantId } = useParams()
  const [searchParams] = useSearchParams()
  const [addWork] = useAddWorkMutation()
  const [searchCrossrefWork] = useSearchWorkMutation()
  const [uploadWorkAttachment] = useUploadWorkAttachmentMutation()

  const [crossrefSearchValue, setCrossrefSearchValue] = useState<string>('')
  const [crossrefSearchType, setCrossrefSearchType] = useState<SearchType>(SearchType.DOI)

  const [title, setTitle] = useState<string>('')
  const [workProductAbstract, setWorkProductAbstract] = useState<string>('')
  const [publisher, setPublisher] = useState<string>('')
  const [doi, setDoi] = useState<string>('')
  const [isbn, setIsbn] = useState<string>('')
  const [url, setUrl] = useState<string>('')
  const [workProductType, setWorkProductType] = useState<string>('')
  const [language, setLanguage] = useState<string>('en')
  const [pages, setPages] = useState<string>('')
  const [volume, setVolume] = useState<string>('')
  const [issue, setIssue] = useState<string>('')
  const [containerTitle, setContainerTitle] = useState<string>('')
  const [publicationDate, setPublicationDate] = useState<string>('')
  const [printedDate, setPrintedDate] = useState<string>('')

  const [workProductId, setWorkProductId] = useState<string>('')

  const [authors, setAuthors] = useState<AuthorCreationData[]>([
    {
      ...defaultAuthorData,
    },
  ])

  const [isApiRequestInProgress, setIsApiRequestInProgress] = useState<boolean>(false)
  const [isSearchWorkApiRequestInProgress, setIsSearchWorkApiRequestInProgress] = useState<boolean>(false)

  const [showForm, setShowForm] = useState<boolean>(false)
  const [isFormEditable, setIsFormEditable] = useState<boolean>(true)

  const [fileList, setFileList] = useState<File[]>([])

  const workType = useMemo(() => {
    return searchParams.get('type') ?? 'manual'
  }, [searchParams])

  useEffect(() => {
    setShowForm(workType === 'manual')
  }, [workType])

  const crossrefSearchCriterias: FilterOption[] = useMemo(
    () => [
      {
        label: 'Select CrossRef search criteria',
        value: '',
      },
      {
        label: 'DOI',
        value: SearchType.DOI,
      },
      {
        label: 'ISBN',
        value: SearchType.ISBN,
      },
    ],
    [workProductType],
  )

  const isFormValid = useMemo(() => {
    if (workProductId) {
      return true
    }

    return (
      title &&
      workProductAbstract &&
      language &&
      publicationDate &&
      authors.length &&
      authors.every((item) => item.firstName && item.lastName && item.role) &&
      authors.some((item) => item.role === 'primary')
    )
  }, [
    title,
    workProductId,
    workProductAbstract,
    publisher,
    language,
    printedDate,
    publicationDate,
    doi,
    isbn,
    url,
    authors,
  ])

  const onAddWorkClicked = useCallback(async () => {
    if (!isFormValid) {
      return
    }

    const request: WorkCreationRequest = {
      workProductId,
      title,
      workProductAbstract,
      grantId,
      workProductType,
      doi,
      isbn,
      url,
      language,
      publisher,
      containerTitle,
      publicationDate: moment(publicationDate, 'YYYY-MM-DD').toDate(),
      printedDate: moment(printedDate, 'YYYY-MM-DD').toDate(),
      authors,
      ...(workProductType === 'journal-article' || workProductType === 'book-chapter'
        ? {
            pages,
            volume,
            issue,
          }
        : {}),
    }

    try {
      setIsApiRequestInProgress(true)
      const workData: WorkData = await addWork({ data: request }).unwrap()

      console.dir(workData)

      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i]

        const formData = new FormData()
        formData.append('files', file, file.name)

        await uploadWorkAttachment({ workId: workData.id, data: formData }).unwrap()
      }

      setIsApiRequestInProgress(false)
      toast('Successfully added')
      navigate(-1)
    } catch (e) {
      console.error(e)
      toast.error('Failed to add the work')
      setIsApiRequestInProgress(false)
    }
  }, [
    title,
    workProductAbstract,
    containerTitle,
    publisher,
    language,
    printedDate,
    publicationDate,
    doi,
    isbn,
    url,
    pages,
    volume,
    issue,
    authors,
    fileList,
  ])

  const addNewAuthor = useCallback(() => {
    const newAuthorList: AuthorCreationData[] = [
      ...authors,
      {
        ...defaultAuthorData,
      },
    ]

    setAuthors(newAuthorList)
  }, [authors])

  const deleteAuthor = useCallback(
    (index: number) => {
      const newAuthorList: AuthorCreationData[] = authors.filter((item: AuthorCreationData, i: number) => i !== index)

      setAuthors(newAuthorList)
    },
    [authors],
  )

  const showPrimaryAuthorHint = useMemo(() => {
    return !authors.some((item) => item.role === 'primary')
  }, [authors])

  const showAuthorHint = useMemo(() => {
    return !authors.every((item) => item.firstName && item.lastName && item.role)
  }, [authors])

  const onSearchCrossrefWorkClicked = useCallback(async () => {
    if (!crossrefSearchValue) {
      return
    }

    try {
      setIsSearchWorkApiRequestInProgress(true)

      const request: WorkSearchRequest = {
        searchKey: crossrefSearchValue,
        searchType: crossrefSearchType,
        page: 0,
        limit: 1,
      }

      const res: WorkData[] = await searchCrossrefWork({ request }).unwrap()

      const workData: WorkData | null = res && res.length ? res[0] : null

      if (workData) {
        setWorkProductId(workData.id)

        setWorkProductType(workData.workProductType)

        if (workData.doi) {
          setDoi(workData.doi)
        }

        if (workData.isbn) {
          setIsbn(workData.isbn)
        }

        if (workData.containerTitle) {
          setContainerTitle(workData.containerTitle)
        }

        if (workData.title) {
          setTitle(workData.title)
        }

        if (workData.language) {
          setLanguage(workData.language)
        }

        if (workData.url) {
          setUrl(workData.url)
        }

        if (workData.publisher) {
          setPublisher(workData.publisher)
        }

        if (workData.page) {
          setPages(workData.page)
        }

        if (workData.volume) {
          setVolume(workData.volume)
        }

        if (workData.issue) {
          setIssue(workData.issue)
        }

        if (workData.publicationDate) {
          setPublicationDate(moment(workData.publicationDate).format('YYYY-MM-DD'))
          setPrintedDate(moment(workData.publicationDate).format('YYYY-MM-DD'))
        }

        const authorList: AuthorData[] = workData.authors ?? []
        const authorCreationList: AuthorCreationData[] = []

        authorList.forEach((author) => {
          authorCreationList.push({
            firstName: author.firstName,
            lastName: author.lastName,
            role: author.role,
            orcId: author.orcId,
          })
        })

        setAuthors(authorCreationList)

        setShowForm(true)
        setIsFormEditable(false)
      }

      setIsSearchWorkApiRequestInProgress(false)
    } catch (e) {
      console.error(e)
      setIsSearchWorkApiRequestInProgress(false)
    }
  }, [crossrefSearchValue, crossrefSearchType])

  const onAttachmentFileSelected = useCallback(
    async (event: { target: HTMLInputElement }) => {
      if (event && event.target && event.target.files && event.target.files[0]) {
        const file = event.target.files[0]

        if (file.size > maxFileSize) {
          toast.error('File is too large')
          return
        }

        setFileList([...fileList, file])
      }
    },
    [fileList],
  )

  const deleteAttachment = useCallback(
    (i: number) => {
      setFileList(fileList.filter((item: File, index: number) => index !== i))
    },
    [fileList],
  )

  return (
    <PrivateLayout>
      <div className="content">
        <OrganizationSummary />

        {workProductType ? <h3 className="mb-4">{convertWorkType(workProductType)}</h3> : null}

        {workType !== 'manual' ? (
          <div className="row">
            <div className="col-12 d-flex justify-content-start align-items-center mb-7">
              <div className="d-flex flex-column">
                <div className="form-floating">
                  <select
                    className="form-select"
                    id="searchCriteria"
                    value={crossrefSearchType}
                    onChange={(e) => {
                      setCrossrefSearchType(e.target.value as SearchType)
                    }}
                  >
                    {crossrefSearchCriterias.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <label htmlFor="searchCriteria">CrossRef search criteria</label>
                </div>
              </div>
              <div className="form-floating ms-3">
                <input
                  className="form-control"
                  id="crossrefSearchValue"
                  type="text"
                  value={crossrefSearchValue}
                  onChange={(e) => {
                    setCrossrefSearchValue(e.target.value)
                  }}
                />
                <label htmlFor="crossrefSearchValue">CrossRef search value </label>
              </div>
              <button
                type="button"
                className="btn btn-primary px-5 ms-3"
                style={{ width: '180px', height: '48px' }}
                disabled={!crossrefSearchValue}
                onClick={() => {
                  onSearchCrossrefWorkClicked()
                }}
              >
                {isSearchWorkApiRequestInProgress ? (
                  <ClipLoader color="white" loading={isSearchWorkApiRequestInProgress} size={15} />
                ) : (
                  'Search in CrossRef'
                )}
              </button>
            </div>
          </div>
        ) : null}

        {showForm ? (
          <div className="row">
            <div className="col-xl-9">
              <form className="row g-3 mb-6">
                <div className="col-12">
                  <div className="form-floating">
                    <select
                      className="form-select"
                      id="workProductType"
                      value={workProductType}
                      onChange={(e) => {
                        setWorkProductType(e.target.value)
                      }}
                      disabled={!isFormEditable}
                    >
                      {workTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <label htmlFor="workProductType">Type</label>
                  </div>
                </div>
                <div className="col-12">
                  <div className="form-floating">
                    <input
                      className="form-control"
                      id="title"
                      type="text"
                      placeholder="Title"
                      value={title}
                      readOnly={!isFormEditable}
                      onChange={(e) => {
                        setTitle(e.target.value)
                      }}
                    />
                    <label htmlFor="title">Title</label>
                  </div>
                </div>
                <div className="col-12">
                  <div className="form-floating">
                    <textarea
                      className="form-control"
                      id="workProductAbstract"
                      placeholder=""
                      style={{ height: '150px' }}
                      value={workProductAbstract}
                      disabled={!isFormEditable}
                      onChange={(e) => {
                        setWorkProductAbstract(e.target.value)
                      }}
                    ></textarea>
                    <label htmlFor="workProductAbstract">Abstract</label>
                  </div>
                </div>
                <div className="col-sm-6 col-md-4 gy-6">
                  <div className="form-floating">
                    <div className="form-floating">
                      <input
                        className="form-control"
                        id="publisher"
                        type="text"
                        placeholder="Publisher"
                        value={publisher}
                        readOnly={!isFormEditable}
                        onChange={(e) => {
                          setPublisher(e.target.value)
                        }}
                      />
                      <label htmlFor="publisher">Publisher</label>
                    </div>
                  </div>
                </div>
                <div className="col-sm-6 col-md-8 gy-6">
                  <div className="form-floating">
                    <input
                      className="form-control"
                      id="containerTitle"
                      type="text"
                      placeholder="Container Title"
                      value={containerTitle}
                      readOnly={!isFormEditable}
                      onChange={(e) => {
                        setContainerTitle(e.target.value)
                      }}
                    />
                    <label htmlFor="containerTitle">Container Title</label>
                  </div>
                </div>
                <div className="col-sm-6 col-md-4">
                  <div className="form-floating">
                    <select
                      className="form-select"
                      id="language"
                      value={language}
                      onChange={(e) => {
                        setLanguage(e.target.value)
                      }}
                      disabled={!isFormEditable}
                    >
                      {languageOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <label htmlFor="language">Language</label>
                  </div>
                </div>
                <div className="col-sm-6 col-md-4">
                  <div className="flatpickr-input-container">
                    <div className="form-floating">
                      <input
                        className="form-control datetimepicker"
                        id="printedDate"
                        type="date"
                        placeholder="Printed date"
                        data-options='{"disableMobile":true}'
                        value={printedDate}
                        readOnly={!isFormEditable}
                        onChange={(e) => {
                          setPrintedDate(e.target.value)
                        }}
                      />
                      <label className="ps-6" htmlFor="printedDate">
                        Printed
                      </label>
                      <IconifyIcon
                        icon="uil:calendar-alt"
                        className="uil uil-calendar-alt flatpickr-icon text-700 me-2"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-sm-6 col-md-4">
                  <div className="flatpickr-input-container">
                    <div className="form-floating">
                      <input
                        className="form-control datetimepicker"
                        id="publicationDate"
                        type="date"
                        placeholder="Published"
                        data-options='{"disableMobile":true}'
                        value={publicationDate}
                        readOnly={!isFormEditable}
                        onChange={(e) => {
                          setPublicationDate(e.target.value)
                        }}
                      />
                      <label className="ps-6" htmlFor="publicationDate">
                        Published
                      </label>
                      <IconifyIcon
                        icon="uil:calendar-alt"
                        className="uil uil-calendar-alt flatpickr-icon text-700 me-2"
                      />
                    </div>
                  </div>
                </div>
                {workProductType === 'journal-article' || workProductType === 'book-chapter' ? (
                  <>
                    <div className="col-sm-6 col-md-4">
                      <div className="form-floating">
                        <input
                          className="form-control"
                          id="pages"
                          type="text"
                          placeholder="Pages"
                          value={pages}
                          readOnly={!isFormEditable}
                          onChange={(e) => {
                            setPages(e.target.value)
                          }}
                        />
                        <label htmlFor="pages">Pages</label>
                      </div>
                    </div>
                    <div className="col-sm-6 col-md-4">
                      <div className="form-floating">
                        <input
                          className="form-control"
                          id="volume"
                          type="text"
                          placeholder="Volume"
                          value={volume}
                          readOnly={!isFormEditable}
                          onChange={(e) => {
                            setVolume(e.target.value)
                          }}
                        />
                        <label htmlFor="volume">Volume</label>
                      </div>
                    </div>
                    <div className="col-sm-6 col-md-4">
                      <div className="form-floating">
                        <input
                          className="form-control"
                          id="issue"
                          type="text"
                          placeholder="Issue"
                          value={issue}
                          readOnly={!isFormEditable}
                          onChange={(e) => {
                            setIssue(e.target.value)
                          }}
                        />
                        <label htmlFor="issue">Issue</label>
                      </div>
                    </div>
                  </>
                ) : null}
                <div className="col-sm-6 col-md-4">
                  <div className="form-floating">
                    <input
                      className="form-control"
                      id="doi"
                      type="text"
                      placeholder="DOI"
                      value={doi}
                      readOnly={!isFormEditable}
                      onChange={(e) => {
                        setDoi(e.target.value)
                      }}
                    />
                    <label htmlFor="doi">DOI</label>
                  </div>
                </div>
                <div className="col-sm-6 col-md-4">
                  <div className="form-floating">
                    <input
                      className="form-control"
                      id="isbn"
                      type="text"
                      placeholder="ISBN"
                      value={isbn}
                      readOnly={!isFormEditable}
                      onChange={(e) => {
                        setIsbn(e.target.value)
                      }}
                    />
                    <label htmlFor="isbn">ISBN</label>
                  </div>
                </div>
                <div className="col-sm-6 col-md-4">
                  <div className="form-floating">
                    <input
                      className="form-control"
                      id="url"
                      type="text"
                      placeholder="URL"
                      value={url}
                      readOnly={!isFormEditable}
                      onChange={(e) => {
                        setUrl(e.target.value)
                      }}
                    />
                    <label htmlFor="url">URL</label>
                  </div>
                </div>
                {workType === 'manual' ? (
                  <div className="row gy-3 mt-3 mb-2 justify-content-between">
                    <h3 className="mb-1 text-1100">Upload Attachments</h3>
                    <div className="import-button-wrapper">
                      <div className="dz-message" data-dz-message="data-dz-message">
                        <img className="me-2" src={uploadIcon} width="25" />
                        Select the file (50 MB maximum)
                      </div>
                      <input className="import-button" type="file" onChange={onAttachmentFileSelected} />
                    </div>
                    {fileList.length > 0 ? (
                      <ol className="mt-4">
                        {fileList.map((fileObj: File, i: number) => (
                          <li key={i} className="d-flex align-items-center">
                            <span>{fileObj.name}</span>
                            <button
                              className="btn-link fw-bold fs--1 ms-3"
                              onClick={() => {
                                deleteAttachment(i)
                              }}
                            >
                              <Trash2 className="text-danger-300 pointer" size={18} />
                            </button>
                          </li>
                        ))}
                      </ol>
                    ) : null}
                  </div>
                ) : null}

                <div className="row align-items-center justify-content-between g-3">
                  <div className="col-auto d-flex align-items-center">
                    <h3 className="mb-0">Author</h3>
                  </div>
                  <div className="col-auto bg-white">
                    {isFormEditable ? (
                      <button
                        type="button"
                        className="btn btn-primary d-flex"
                        style={{ color: 'white', width: '200px' }}
                        onClick={() => {
                          addNewAuthor()
                        }}
                      >
                        <UserPlus size={16} />
                        <span className="ms-3">Add Author</span>
                      </button>
                    ) : null}
                  </div>
                </div>
                <div className="row col-12 pb-6 g-3">
                  {authors.map((author: AuthorCreationData, i: number) => (
                    <>
                      <div className="col-sm-6 col-md-4">
                        <div className="form-floating">
                          <input
                            className="form-control"
                            id="firstName"
                            type="text"
                            placeholder="First Name"
                            value={author.firstName}
                            readOnly={!isFormEditable}
                            onChange={(e) => {
                              setAuthors((authorList) =>
                                authorList.map((item: AuthorCreationData, index: number) =>
                                  index === i
                                    ? {
                                        ...item,
                                        firstName: e.target.value,
                                      }
                                    : item,
                                ),
                              )
                            }}
                          />
                          <label htmlFor="firstName">First Name</label>
                        </div>
                      </div>
                      <div className="col-sm-6 col-md-4">
                        <div className="form-floating">
                          <input
                            className="form-control"
                            id="lastName"
                            type="text"
                            placeholder="Last Name"
                            value={author.lastName}
                            readOnly={!isFormEditable}
                            onChange={(e) => {
                              setAuthors((authorList) =>
                                authorList.map((item: AuthorCreationData, index: number) =>
                                  index === i
                                    ? {
                                        ...item,
                                        lastName: e.target.value,
                                      }
                                    : item,
                                ),
                              )
                            }}
                          />
                          <label htmlFor="lastName">Last Name</label>
                        </div>
                      </div>
                      <div className="col-sm-6 col-md-4">
                        <div className="form-floating">
                          <select
                            className="form-select"
                            id="role"
                            value={author.role}
                            disabled={!isFormEditable}
                            onChange={(e) => {
                              setAuthors((authorList) =>
                                authorList.map((item: AuthorCreationData, index: number) =>
                                  index === i
                                    ? {
                                        ...item,
                                        role: e.target.value,
                                      }
                                    : item,
                                ),
                              )
                            }}
                          >
                            {roleOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <label htmlFor="role">Role</label>
                        </div>
                      </div>
                      <div className="col-sm-6 col-md-4">
                        <div className="form-floating">
                          <input
                            className="form-control"
                            id="orcId"
                            type="text"
                            placeholder="ORCID"
                            value={author.orcId}
                            readOnly={!isFormEditable}
                            onChange={(e) => {
                              setAuthors((authorList) =>
                                authorList.map((item: AuthorCreationData, index: number) =>
                                  index === i
                                    ? {
                                        ...item,
                                        orcId: e.target.value,
                                      }
                                    : item,
                                ),
                              )
                            }}
                          />
                          <label htmlFor="orcId">ORCID</label>
                        </div>
                      </div>
                      <div className="col-sm-6 col-md-4">
                        {authors.length > 1 && isFormEditable ? (
                          <button
                            className="btn-link fw-bold fs--1 mt-4 me-3"
                            onClick={() => {
                              deleteAuthor(i)
                            }}
                          >
                            <UserMinus className="text-danger-300" size={16} />
                          </button>
                        ) : null}
                      </div>
                      <hr className="bg-200 mb-4 mt-4" />
                    </>
                  ))}
                  {showPrimaryAuthorHint ? (
                    <div className="hint error mt-3">At least one primary author is required for the grant</div>
                  ) : null}
                  {showAuthorHint ? <div className="hint error mt-3">At least 1 author is not fully filled</div> : null}
                </div>
                <div className="col-12 gy-6">
                  <div className="row g-3 justify-content-end">
                    <div className="col-auto">
                      <button
                        type="button"
                        className="btn btn-phoenix-primary px-5"
                        onClick={() => {
                          navigate(-1)
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                    <div className="col-auto">
                      <button
                        type="button"
                        className="btn btn-primary px-5 px-sm-15"
                        disabled={!isFormValid}
                        onClick={() => {
                          onAddWorkClicked()
                        }}
                      >
                        {isApiRequestInProgress ? (
                          <ClipLoader color="white" loading={isApiRequestInProgress} size={15} />
                        ) : (
                          'Create Work'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        ) : null}

        <Footer />
      </div>
    </PrivateLayout>
  )
}

export default AddWork
